/**
 * "Score without an account" (PRD ID-6), remembered across reloads.
 *
 * ADR-0008 put a login in front of the pad and said why — a scorer must find his matches
 * tomorrow, and a browser-only session survives neither a cleared cookie nor a new phone.
 * What it did not settle is the person who is handed a phone at the ground and told to
 * score, who has no account, no interest in making one, and a first over starting. Turning
 * him away is how the app loses the match it was opened for.
 *
 * So a guest scores locally: the ledger is on this device exactly as it is for anyone else
 * (#13), and nothing is published. What he does not get is anything that is *about* an
 * account — the match list, a profile, a tournament — which is `screenFor`'s decision, not
 * this file's.
 *
 * A flag in `localStorage` rather than a cookie, because there is no server session to name.
 * `sessionStorage` would be wrong for the same reason a reload must not lose a match: a
 * scorer whose tab is killed at the drinks break has to come back to the same state.
 */
import type { Store } from "../localStore.ts";

const KEY = "sportsos.guest";

export function isGuest(store: Store): boolean {
  try {
    return store.getItem(KEY) === "1";
  } catch {
    // A browser refusing storage falls back to being asked to sign in, which is the safe
    // direction: it costs a tap, where the other way round would hide the login from someone
    // who has an account.
    return false;
  }
}

export function beginGuest(store: Store): void {
  try {
    store.setItem(KEY, "1");
  } catch {
    // The choice still holds for this page — `App` keeps it in state — it just will not
    // survive a reload. Better than refusing to start the match.
  }
}

/** Cleared when the guest signs in or signs up, so the account takes over from here. */
export function endGuest(store: Store): void {
  try {
    store.setItem(KEY, "0");
  } catch {
    // See above.
  }
}
